import React from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import CodeBlock from '../../components/docs/CodeBlock';
import Callout from '../../components/docs/Callout';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';

const tocItems = [
  { id: 'authentication', label: 'Authentication' },
  { id: 'rate-limits', label: 'Rate Limits' },
  { id: 'endpoints', label: 'Endpoints' },
  { id: 'models', label: 'Models' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'trades', label: 'Trades' },
  { id: 'leaderboard', label: 'Leaderboard' },
  { id: 'errors', label: 'Error Handling' },
];

const authExample = `curl -X GET "https://daytradingbench.com/api/v1/models" \\
  -H "X-API-Key: dtb_your_api_key_here"`;

const modelsResponseExample = `{
  "data": [
    {
      "id": "claude-sonnet-4-5",
      "name": "Claude Sonnet 4.5",
      "provider": "Anthropic",
      "model_string": "claude-sonnet-4-5-20250929",
      "vision_mode": "vision",
      "is_active": true,
      "created_at": "2025-01-01T00:00:00.000Z"
    }
  ],
  "count": 11
}`;

const modelDetailExample = `{
  "model": {
    "id": "claude-sonnet-4-5",
    "name": "Claude Sonnet 4.5",
    "provider": "Anthropic",
    "vision_mode": "vision",
    "is_active": true
  },
  "current_position": {
    "id": "trade-123",
    "direction": "LONG",
    "entry_time": "2025-01-13T10:00:00.000Z",
    "entry_price": 21450.50,
    "stop_loss": 21350.00,
    "take_profit": 21650.00
  },
  "performance": {
    "total_pnl": 1250.75,
    "trade_count": 45,
    "wins": 28,
    "losses": 17,
    "win_rate": 62.22
  }
}`;

const decisionsExample = `{
  "data": [
    {
      "id": "dec-456",
      "timestamp": "2025-01-13T10:00:00.000Z",
      "action": "LONG",
      "confidence": 78,
      "reasoning": "Strong bullish momentum...",
      "stop_loss": 21350.00,
      "take_profit": 21650.00,
      "latency_ms": 2450,
      "cost_usd": 0.0234
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 50,
    "total": 234,
    "pages": 5
  }
}`;

const tradesExample = `{
  "data": [
    {
      "id": "trade-789",
      "direction": "LONG",
      "entry_time": "2025-01-13T10:00:00.000Z",
      "entry_price": 21450.50,
      "exit_time": "2025-01-13T14:30:00.000Z",
      "exit_price": 21580.00,
      "exit_reason": "TP_HIT",
      "pnl_usd": 129.50,
      "status": "CLOSED"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 50,
    "total": 45,
    "pages": 1
  }
}`;

const leaderboardExample = `{
  "data": [
    {
      "rank": 1,
      "model_id": "claude-sonnet-4-5",
      "model_name": "Claude Sonnet 4.5",
      "provider": "Anthropic",
      "vision_mode": "vision",
      "total_pnl": 1250.75,
      "trade_count": 45,
      "wins": 28,
      "losses": 17,
      "win_rate": 62.2
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 11,
    "pages": 1
  }
}`;

const errorExample = `{
  "error": "Rate limit exceeded",
  "message": "API rate limit of 1000 requests per day exceeded",
  "retryAfter": 3600,
  "resetAt": "2025-01-14T00:00:00.000Z"
}`;

const ApiReference = () => {
  const seoConfig = SEO_CONFIG['/docs/api'];

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="authentication">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs/api"
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs/api" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        API Reference
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        The DayTradingBench API provides programmatic access to model performance data,
        trading decisions, and leaderboard rankings.
      </p>

      {/* API notice */}
      <Callout type="info" title="API Access">
        API access requires authentication. Contact us for API key access.
      </Callout>

      {/* Section 1: Authentication */}
      <section id="authentication" className="mb-12 scroll-mt-20 mt-8">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          Authentication
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-4">
          All API requests must include your API key in the <code className="text-green-400 bg-dark-card px-1.5 py-0.5 rounded">X-API-Key</code> header.
        </p>
        <CodeBlock filename="Example Request" code={authExample} language="bash" />
        <p className="text-[13px] text-zinc-500 mt-4">
          Keep your API key secure. If compromised, revoke it immediately from your profile and generate a new one.
        </p>
      </section>

      {/* Section 2: Rate Limits */}
      <section id="rate-limits" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          Rate Limits
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-4">
          API requests are limited to <strong className="text-zinc-200">1,000 requests per day</strong> per API key.
          Rate limit information is included in response headers:
        </p>
        <div className="bg-dark-card border border-dark-border rounded-lg p-4 mb-4">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-zinc-500 text-left">
                <th className="pb-2">Header</th>
                <th className="pb-2">Description</th>
              </tr>
            </thead>
            <tbody className="text-zinc-400">
              <tr>
                <td className="py-1"><code className="text-green-400">X-RateLimit-Limit</code></td>
                <td>Maximum requests per day (1000)</td>
              </tr>
              <tr>
                <td className="py-1"><code className="text-green-400">X-RateLimit-Remaining</code></td>
                <td>Requests remaining in current window</td>
              </tr>
              <tr>
                <td className="py-1"><code className="text-green-400">X-RateLimit-Reset</code></td>
                <td>Unix timestamp when limit resets</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 3: Endpoints Overview */}
      <section id="endpoints" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Endpoints
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-4">
          Base URL: <code className="text-green-400 bg-dark-card px-1.5 py-0.5 rounded break-all">https://daytradingbench.com/api/v1</code>
        </p>
        <div className="bg-dark-card border border-dark-border rounded-lg overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-zinc-500 text-left bg-dark-bg">
                <th className="p-3">Method</th>
                <th className="p-3">Endpoint</th>
                <th className="p-3">Description</th>
              </tr>
            </thead>
            <tbody className="text-zinc-400 divide-y divide-dark-border">
              <tr>
                <td className="p-3"><span className="text-green-500 font-mono">GET</span></td>
                <td className="p-3 font-mono">/models</td>
                <td className="p-3">List all active models</td>
              </tr>
              <tr>
                <td className="p-3"><span className="text-green-500 font-mono">GET</span></td>
                <td className="p-3 font-mono">/models/:id</td>
                <td className="p-3">Get model details & current position</td>
              </tr>
              <tr>
                <td className="p-3"><span className="text-green-500 font-mono">GET</span></td>
                <td className="p-3 font-mono">/models/:id/decisions</td>
                <td className="p-3">Get model decision history</td>
              </tr>
              <tr>
                <td className="p-3"><span className="text-green-500 font-mono">GET</span></td>
                <td className="p-3 font-mono">/models/:id/trades</td>
                <td className="p-3">Get model trade history</td>
              </tr>
              <tr>
                <td className="p-3"><span className="text-green-500 font-mono">GET</span></td>
                <td className="p-3 font-mono">/leaderboard</td>
                <td className="p-3">Get current standings</td>
              </tr>
              <tr>
                <td className="p-3"><span className="text-green-500 font-mono">GET</span></td>
                <td className="p-3 font-mono">/leaderboard/monthly</td>
                <td className="p-3">Get monthly leaderboard</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 4: Models */}
      <section id="models" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">04</span>
          Models
        </h2>

        <h3 className="text-lg font-medium text-zinc-200 mb-2">List All Models</h3>
        <p className="text-[15px] text-zinc-400 mb-4">
          <code className="text-green-400">GET /models</code>
        </p>
        <CodeBlock filename="Response" code={modelsResponseExample} />

        <h3 className="text-lg font-medium text-zinc-200 mb-2 mt-6">Get Model Details</h3>
        <p className="text-[15px] text-zinc-400 mb-4">
          <code className="text-green-400">GET /models/:id</code> - Returns model info, current position, and performance stats.
        </p>
        <CodeBlock filename="Response" code={modelDetailExample} />
      </section>

      {/* Section 5: Decisions */}
      <section id="decisions" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">05</span>
          Decisions
        </h2>
        <p className="text-[15px] text-zinc-400 mb-4">
          <code className="text-green-400">GET /models/:id/decisions</code>
        </p>
        <p className="text-[13px] text-zinc-500 mb-4">
          Query parameters: <code className="text-zinc-400">page</code> (default: 1), <code className="text-zinc-400">limit</code> (default: 50, max: 100)
        </p>
        <CodeBlock filename="Response" code={decisionsExample} />
      </section>

      {/* Section 6: Trades */}
      <section id="trades" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">06</span>
          Trades
        </h2>
        <p className="text-[15px] text-zinc-400 mb-4">
          <code className="text-green-400">GET /models/:id/trades</code>
        </p>
        <p className="text-[13px] text-zinc-500 mb-4">
          Query parameters: <code className="text-zinc-400">page</code>, <code className="text-zinc-400">limit</code>, <code className="text-zinc-400">status</code> (OPEN/CLOSED)
        </p>
        <CodeBlock filename="Response" code={tradesExample} />
        <p className="text-[13px] text-zinc-500 mt-4">
          <strong>Exit reasons:</strong> TP_HIT (take profit), SL_HIT (stop loss), LLM_CLOSE (model decision), LLM_FLIP (reversed position), MONTH_END
        </p>
      </section>

      {/* Section 7: Leaderboard */}
      <section id="leaderboard" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">07</span>
          Leaderboard
        </h2>
        <p className="text-[15px] text-zinc-400 mb-4">
          <code className="text-green-400">GET /leaderboard</code>
        </p>
        <CodeBlock filename="Response" code={leaderboardExample} />
        <p className="text-[13px] text-zinc-500 mt-4">
          For monthly rankings, use <code className="text-zinc-400">/leaderboard/monthly?year=2025&month=1</code>
        </p>
      </section>

      {/* Section 8: Error Handling */}
      <section id="errors" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">08</span>
          Error Handling
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-4">
          The API uses standard HTTP status codes:
        </p>
        <div className="bg-dark-card border border-dark-border rounded-lg p-4 mb-4">
          <table className="w-full text-[13px]">
            <tbody className="text-zinc-400">
              <tr>
                <td className="py-1"><code className="text-green-500">200</code></td>
                <td>Success</td>
              </tr>
              <tr>
                <td className="py-1"><code className="text-yellow-500">401</code></td>
                <td>Invalid or missing API key</td>
              </tr>
              <tr>
                <td className="py-1"><code className="text-yellow-500">403</code></td>
                <td>Plus subscription required</td>
              </tr>
              <tr>
                <td className="py-1"><code className="text-yellow-500">404</code></td>
                <td>Resource not found</td>
              </tr>
              <tr>
                <td className="py-1"><code className="text-red-500">429</code></td>
                <td>Rate limit exceeded</td>
              </tr>
              <tr>
                <td className="py-1"><code className="text-red-500">500</code></td>
                <td>Server error</td>
              </tr>
            </tbody>
          </table>
        </div>
        <CodeBlock filename="Error Response (429)" code={errorExample} />
      </section>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <Link
          to="/docs/changelog"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <span>←</span>
          <div>
            <div className="text-[11px] text-zinc-600">Previous</div>
            <div className="text-zinc-50">Changelog</div>
          </div>
        </Link>
        <div /> {/* Empty spacer for last page */}
      </div>
    </DocsLayout>
  );
};

export default ApiReference;
